import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery, useRemoveQueries } from '@/hooks';
import type { z } from 'zod';

import {
  UpdateWorkflowTemplateParamsSchema,
  WorkflowTemplateFormSchema,
  WorkflowTemplateModel,
  WorkflowTemplatesModel,
  WorkflowTemplatesWithPaginationModel,
} from '../models/template';
import {
  workflowInvalidateQueryKeyMap,
  workflowQueryKeyMap,
} from '../utils/workflow-query-keys';

const API_BASE_URL = '/api/workflows/templates/';

/** 所属組織が利用組織に設定されているテンプレートのみを取得する。*/
export const useWorkflowTemplatesByJoinOrganization = <
  T = z.infer<typeof WorkflowTemplatesModel>,
>(
  select?: (data: z.infer<typeof WorkflowTemplatesModel>) => T,
) => {
  return useQuery({
    queryKey: workflowQueryKeyMap.templates(),
    queryFn: async () => {
      const res = await axiosGet(API_BASE_URL);
      return WorkflowTemplatesModel.parse(res);
    },
    select,
  });
};
export const useWorkflowTemplatesWithPagination = <
  T = z.infer<typeof WorkflowTemplatesWithPaginationModel>,
>(
  params: {
    page: number;
    pageSize: number;
    cascader?: string | null;
    q?: string | null;
  },
  select?: (data: z.infer<typeof WorkflowTemplatesWithPaginationModel>) => T,
) => {
  return useQuery({
    queryKey: workflowQueryKeyMap.templatesWithPagination(params),
    queryFn: async () => {
      const res = await axiosGet(
        `${API_BASE_URL}list-with-pagination/`,
        params,
      );
      return WorkflowTemplatesWithPaginationModel.parse(res);
    },
    select,
  });
};

export const useWorkflowTemplate = (id: string) => {
  return useQuery({
    queryKey: workflowQueryKeyMap.template(id),
    queryFn: async () => {
      const res = await axiosGet(`${API_BASE_URL}${id}/`);
      return WorkflowTemplateModel.parse(res);
    },
    enabled: !!id,
  });
};

export const useCreateWorkflowTemplate = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: z.infer<typeof WorkflowTemplateFormSchema>) => {
      const { name, organizationIds, ...data } =
        WorkflowTemplateFormSchema.parse(values);
      return await axiosPost(API_BASE_URL, {
        name,
        data,
        organizationIds: organizationIds.flatMap((ids) => ids.at(-1) ?? []),
      });
    },
    onSuccess: async () => {
      await Promise.all(
        workflowInvalidateQueryKeyMap
          .updateTemplateList()
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
    },
  });
};

export const useUpdateWorkflowTemplate = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof UpdateWorkflowTemplateParamsSchema>,
    ) => {
      const { id, name, organizationIds, ...data } =
        UpdateWorkflowTemplateParamsSchema.parse(values);
      return await axiosPatch(`${API_BASE_URL}${id}/`, {
        name,
        organizationIds: organizationIds.flatMap((ids) => ids.at(-1) ?? []),
        data,
      });
    },
    onSuccess: async (_, { id }) => {
      await Promise.all(
        workflowInvalidateQueryKeyMap
          .updateTemplateList(id)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
    },
  });
};

export const useDeleteWorkflowTemplate = () => {
  const { invalidate } = useInvalidateQuery();
  const { removeQuery } = useRemoveQueries();

  return useMutation({
    mutationFn: async (id: string) => {
      return await axiosDelete(`${API_BASE_URL}${id}/`);
    },
    onSuccess: async (_, id) => {
      await Promise.all(
        workflowInvalidateQueryKeyMap
          .updateTemplateList(id)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      removeQuery(workflowQueryKeyMap.template(id));
    },
  });
};
